import { AvailableExchange, Exchange, UserExchange, UserExchangeAccount } from '@crypto-trading/common/browser';
import { ApiClientManager } from '@/utils/api-client-factory';

/**
 * Exchange API functions - organized by domain
 */

/**
 * Get all available exchanges
 */
export const getAvailableExchanges = async (): Promise<AvailableExchange[]> => {
  return ApiClientManager.getExchangeClient().getAvailableExchanges();
};

/**
 * Get user's configured exchanges
 */
export const getUserExchanges = async (
  userId: string,
  includeBalances: boolean = false
): Promise<UserExchange[]> => {
  return ApiClientManager.getExchangeClient().getUserExchanges(userId, includeBalances);
};

/**
 * Get user's active exchange
 */
export const getUserActiveExchange = async (userId: string): Promise<{
  exchange: Exchange;
  account: UserExchangeAccount;
} | null> => {
  return ApiClientManager.getExchangeClient().getUserActiveExchange(userId);
};

/**
 * Setup exchange credentials
 */
export const setupExchange = async (
  userId: string,
  exchangeCode: string,
  apiKey: string,
  apiSecret: string,
  apiPassphrase?: string
): Promise<void> => {
  return ApiClientManager.getExchangeClient().setupExchange(
    userId,
    exchangeCode,
    apiKey,
    apiSecret,
    apiPassphrase
  );
};

/**
 * Set active exchange for user
 */
export const setActiveExchange = async (userId: string, exchangeCode: string): Promise<void> => {
  return ApiClientManager.getExchangeClient().setActiveExchange(userId, exchangeCode);
};

/**
 * Reorder connected exchanges (index 0 = first-choice autotrade venue).
 */
export const reorderExchanges = async (
  userId: string,
  exchangeCodes: string[]
): Promise<void> => {
  return ApiClientManager.getExchangeClient().reorderExchanges(userId, exchangeCodes);
};

/**
 * Remove exchange for user
 */
export const removeExchange = async (userId: string, exchangeCode: string): Promise<void> => {
  return ApiClientManager.getExchangeClient().removeExchange(userId, exchangeCode);
};

/**
 * Force-refresh balance for a single exchange (30-min server-side throttle).
 * Throws a BalanceRefreshThrottledApiError (status 429) when the throttle is active.
 */
export const refreshBalance = async (userId: string, exchangeCode: string) => {
  return ApiClientManager.getExchangeClient().refreshBalance(userId, exchangeCode);
};

